import type { PathBorderContinuation } from '../../model/gamestate/GameState';

const HEX_NEIGHBOR_OFFSETS_EVEN = [
  { x: 0, y: -1 },
  { x: 1, y: -1 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
  { x: -1, y: -1 },
] as const;

const HEX_NEIGHBOR_OFFSETS_ODD = [
  { x: 0, y: -1 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
  { x: -1, y: 1 },
  { x: -1, y: 0 },
] as const;

function isWithinMap(width: number, height: number, x: number, y: number): boolean {
  return x >= 0 && x < width && y >= 0 && y < height;
}

function squareQueriesCoordinate(cellX: number, cellY: number, targetX: number, targetY: number): boolean {
  const dx = targetX - cellX;
  const dy = targetY - cellY;
  return !(dx === 0 && dy === 0) && Math.abs(dx) <= 1 && Math.abs(dy) <= 1;
}

function hexQueriesCoordinate(cellX: number, cellY: number, targetX: number, targetY: number): boolean {
  const offsets = (cellX & 1) === 1 ? HEX_NEIGHBOR_OFFSETS_ODD : HEX_NEIGHBOR_OFFSETS_EVEN;
  return offsets.some((offset) => cellX + offset.x === targetX && cellY + offset.y === targetY);
}

function triangleQueriesCoordinate(cellX: number, cellY: number, targetX: number, targetY: number): boolean {
  const up = ((cellX + cellY) & 1) === 0;
  return (
    (!up && targetX === cellX && targetY === cellY - 1)
    || (targetX === cellX + 1 && targetY === cellY)
    || (up && targetX === cellX && targetY === cellY + 1)
    || (targetX === cellX - 1 && targetY === cellY)
  );
}

export function isValidPathBorderContinuationCoordinate(
  gridType: string,
  width: number,
  height: number,
  targetX: number,
  targetY: number,
): boolean {
  if (isWithinMap(width, height, targetX, targetY)) {
    return false;
  }

  const minX = Math.max(0, targetX - 1);
  const maxX = Math.min(width - 1, targetX + 1);
  const minY = Math.max(0, targetY - 1);
  const maxY = Math.min(height - 1, targetY + 1);
  for (let cellY = minY; cellY <= maxY; cellY += 1) {
    for (let cellX = minX; cellX <= maxX; cellX += 1) {
      if (
        (gridType === 'square' && squareQueriesCoordinate(cellX, cellY, targetX, targetY))
        || (gridType === 'hex' && hexQueriesCoordinate(cellX, cellY, targetX, targetY))
        || (gridType === 'triangle' && triangleQueriesCoordinate(cellX, cellY, targetX, targetY))
      ) {
        return true;
      }
    }
  }
  return false;
}

export function hasPathBorderContinuation(
  continuations: readonly PathBorderContinuation[] | undefined,
  atlasId: string,
  x: number,
  y: number,
): boolean {
  return Boolean(continuations?.some((entry) => entry.atlasId === atlasId && entry.x === x && entry.y === y));
}

export function canonicalizePathBorderContinuations(
  gridType: string,
  width: number,
  height: number,
  continuations: readonly PathBorderContinuation[] | null | undefined,
): PathBorderContinuation[] {
  if (!Array.isArray(continuations) || width <= 0 || height <= 0) {
    return [];
  }
  const deduped = new Map<string, PathBorderContinuation>();
  for (const entry of continuations) {
    if (!entry || typeof entry.atlasId !== 'string') {
      continue;
    }
    const x = Number.isFinite(entry.x) ? Math.trunc(entry.x) : Number.NaN;
    const y = Number.isFinite(entry.y) ? Math.trunc(entry.y) : Number.NaN;
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      continue;
    }
    if (!isValidPathBorderContinuationCoordinate(gridType, width, height, x, y)) {
      continue;
    }
    const atlasId = entry.atlasId.trim();
    if (!atlasId) {
      continue;
    }
    deduped.set(`${atlasId}:${x}:${y}`, { atlasId, x, y });
  }
  return Array.from(deduped.values());
}
